import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  Alert,
  Divider,
  Avatar,
  Fade,
  Chip,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import HomeIcon from '@mui/icons-material/Home';
import BusinessIcon from '@mui/icons-material/Business';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DiamondIcon from '@mui/icons-material/Diamond';

// Premium Color Palette
const colors = {
  navy: '#0a1128',
  darkNavy: '#060b1f',
  gold: '#c9a03d',
  lightGold: '#d4af37',
  darkGold: '#a0782c',
  charcoal: '#1a1a2e',
  lightCharcoal: '#16213e',
  pearl: '#f5f5f0',
  darkPearl: '#e8e8e0',
  white: '#ffffff',
  textDark: '#1a1a2e',
  textLight: '#6b6b6b',
  premiumDark: '#0d1117',
  premiumGold: '#c9a03d',
};

const Applications = () => {
  const [agree, setAgree] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [applyOnBehalf, setApplyOnBehalf] = useState('No');

  const handleSubmit = () => {
    if (agree === 'I AGREE' && consentChecked) {
      alert('✓ Application Submitted Successfully!\n\nThank you for choosing Diamond Card. A relationship manager will contact you within 48 hours.');
    } else {
      alert('Please review and agree to the terms and conditions before submitting.');
    }
  };

  return (
    <Box sx={{ bgcolor: '#faf9f7', minHeight: '100vh' }}>
      {/* Premium Hero Section */}
      <Box 
        sx={{ 
          position: 'relative',
          py: { xs: 6, md: 8 }, 
          color: 'white', 
          textAlign: 'center',
          background: `linear-gradient(135deg, ${colors.navy} 0%, ${colors.darkNavy} 100%)`,
          borderBottom: `3px solid ${colors.gold}`,
        }}
      >
        <Container sx={{ position: 'relative', zIndex: 1 }}>
          <Fade in timeout={1000}>
            <Box>
              <Avatar 
                src="/logo/logo.png" 
                alt="Diamond Card"
                sx={{ 
                  width: 90, 
                  height: 90, 
                  mx: 'auto', 
                  mb: 2,
                  backgroundColor: 'transparent',
                  '& img': { objectFit: 'contain' }
                }}
                variant="square"
              />
              <Typography 
                variant="h2" 
                sx={{ 
                  fontSize: { xs: '1.6rem', md: '2.2rem' }, 
                  fontWeight: 800, 
                  color: colors.gold, 
                  mb: 1, 
                  letterSpacing: 2,
                  fontFamily: "'Cinzel', serif",
                }}
              >
                DIAMOND CARD
              </Typography>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 600, 
                  color: colors.white, 
                  mb: 2, 
                  fontSize: { xs: '1.2rem', md: '1.5rem' },
                  fontFamily: "'Cinzel', serif",
                }}
              >
                Application Form
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.8)', maxWidth: 600, mx: 'auto' }}>
                Please complete all sections below to apply for your premium Diamond Card
              </Typography>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Complete Application Form - All Sections on One Page */}
      <Container sx={{ py: 4 }}>
        <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 3, boxShadow: '0 10px 40px rgba(0,0,0,0.1)' }}>
          
          {/* Section 1: User Details */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.gold}` }}>
              <DiamondIcon sx={{ color: colors.gold, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, fontFamily: "'Cinzel', serif" }}>
                Section 1: Account Setup
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Application Type*</InputLabel>
                  <Select defaultValue="debit" sx={{ borderRadius: 2 }}>
                    <MenuItem value="debit">💎 Diamond Debit Card</MenuItem>
                    <MenuItem value="credit">💳 Diamond Credit Card</MenuItem>
                    <MenuItem value="prepaid">🎁 Diamond Prepaid Card</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Username*" fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Email Address*" type="email" fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Password*" type="password" fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Confirm Password*" type="password" fullWidth required sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 2: Personal Information */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.gold}` }}>
              <PersonIcon sx={{ color: colors.gold, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, fontFamily: "'Cinzel', serif" }}>
                Section 2: Personal Information
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Title*</InputLabel>
                  <Select defaultValue="Mr">
                    <MenuItem value="Mr">Mr</MenuItem>
                    <MenuItem value="Ms">Ms</MenuItem>
                    <MenuItem value="Mrs">Mrs</MenuItem>
                    <MenuItem value="Dr">Dr</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label="Title (other)" fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Nickname" fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="First Name*" fullWidth required />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Middle Name(s)" fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Last Name(s)*" fullWidth required helperText="As shown on your passport/ID" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Identity / Passport / Social Security Number*" fullWidth required />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Nationality*</InputLabel>
                  <Select defaultValue="US">
                    <MenuItem value="US">United States</MenuItem>
                    <MenuItem value="UK">United Kingdom</MenuItem>
                    <MenuItem value="ZA">South Africa</MenuItem>
                    <MenuItem value="AF">Afghanistan</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Date of Birth*" type="date" fullWidth InputLabelProps={{ shrink: true }} required />
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Marital Status</InputLabel>
                  <Select defaultValue="single">
                    <MenuItem value="single">Single</MenuItem>
                    <MenuItem value="married">Married</MenuItem>
                    <MenuItem value="divorced">Divorced</MenuItem>
                    <MenuItem value="widowed">Widowed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <TextField label="Number of dependants" type="number" fullWidth defaultValue="0" />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 3: Contact Details */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.gold}` }}>
              <PhoneIcon sx={{ color: colors.gold, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, fontFamily: "'Cinzel', serif" }}>
                Section 3: Contact Details
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField label="Main Phone Number*" fullWidth required placeholder="+1 (555) 555-5555" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Alternate Phone Number" fullWidth placeholder="+1 (555) 555-5555" />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Fax Number" fullWidth />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 4: Residential Address */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.gold}` }}>
              <HomeIcon sx={{ color: colors.gold, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, fontFamily: "'Cinzel', serif" }}>
                Section 4: Residential Address
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField label="Street No. / Apt / Suite" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Residential Address (PO Box is not valid)*" fullWidth required />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="City*" fullWidth required />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="State / Province*" fullWidth required />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Postal Code*" fullWidth required />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Country*</InputLabel>
                  <Select defaultValue="US">
                    <MenuItem value="US">United States</MenuItem>
                    <MenuItem value="ZA">South Africa</MenuItem>
                    <MenuItem value="UK">United Kingdom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Residential Status</InputLabel>
                  <Select defaultValue="homeowner">
                    <MenuItem value="homeowner">Homeowner (with mortgage)</MenuItem>
                    <MenuItem value="renter">Renter</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Mortgage / Rent (per month)" type="number" fullWidth defaultValue="0" />
              </Grid>
              <Grid item xs={12}>
                <TextField label="When did you move here?" type="text" placeholder="MM / YYYY" fullWidth />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 5: Postal Address */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.gold}` }}>
              <HomeIcon sx={{ color: colors.gold, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, fontFamily: "'Cinzel', serif" }}>
                Section 5: Postal Address
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField label="Postal Address" fullWidth defaultValue="P.O. Box 5555" />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="City" fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="State / Province" fullWidth />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField label="Postal Code" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select defaultValue="US">
                    <MenuItem value="US">United States</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 6: Employment Status */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.gold}` }}>
              <BusinessIcon sx={{ color: colors.gold, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, fontFamily: "'Cinzel', serif" }}>
                Section 6: Employment Information
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Employment Status</InputLabel>
                  <Select defaultValue="fulltime">
                    <MenuItem value="fulltime">Employed Full-time</MenuItem>
                    <MenuItem value="parttime">Employed Part-time</MenuItem>
                    <MenuItem value="self">Self-employed</MenuItem>
                    <MenuItem value="unemployed">Unemployed</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Employer" fullWidth />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Estimated Annual Income" fullWidth placeholder="$0.00" />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 7: Business Application */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.gold}` }}>
              <BusinessIcon sx={{ color: colors.gold, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, fontFamily: "'Cinzel', serif" }}>
                Section 7: Business Application
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel sx={{ color: colors.textLight }}>I am applying on behalf of my Company / Business</FormLabel>
                  <RadioGroup row value={applyOnBehalf} onChange={(e) => setApplyOnBehalf(e.target.value)}>
                    <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                    <FormControlLabel value="No" control={<Radio />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 8: Business Details */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.gold}` }}>
              <BusinessIcon sx={{ color: colors.gold, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, fontFamily: "'Cinzel', serif" }}>
                Section 8: Corporate Information
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField label="Trading Name" fullWidth helperText="Business Name As You Want It To Appear On The Card" />
              </Grid>
              <Grid item xs={12}>
                <TextField label="Registered Company Name" fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Company Registration Number" fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Business Legal Structure</InputLabel>
                  <Select defaultValue="sole">
                    <MenuItem value="sole">Sole Proprietorship</MenuItem>
                    <MenuItem value="llc">LLC</MenuItem>
                    <MenuItem value="corp">Corporation</MenuItem>
                    <MenuItem value="partnership">Partnership</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Business Tax ID Number (TIN)" fullWidth />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="VAT / TAX Registration Number" fullWidth helperText="check if applicable" />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>How old is the business?</InputLabel>
                  <Select defaultValue="less1">
                    <MenuItem value="less1">Less than 1 year</MenuItem>
                    <MenuItem value="1-3">1-3 years</MenuItem>
                    <MenuItem value="3-5">3-5 years</MenuItem>
                    <MenuItem value="5+">5+ years</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Website" fullWidth placeholder="https://www.yourcompany.com" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField label="Business Telephone Number" fullWidth placeholder="+1 (555) 555-5555" />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Sector / Industry</InputLabel>
                  <Select>
                    <MenuItem value="tech">Technology</MenuItem>
                    <MenuItem value="finance">Finance</MenuItem>
                    <MenuItem value="retail">Retail</MenuItem>
                    <MenuItem value="realestate">Real Estate</MenuItem>
                    <MenuItem value="other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 9: Financial Information */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.gold}` }}>
              <AttachMoneyIcon sx={{ color: colors.gold, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, fontFamily: "'Cinzel', serif" }}>
                Section 9: Financial Information
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField label="Annual Revenue" fullWidth placeholder="$0.00" />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField 
                  label="Average Monthly Expenditure" 
                  fullWidth 
                  helperText="Approximately how much does your business spend using credit cards per month?" 
                  placeholder="$0.00"
                />
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 10: Sureties */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.gold}` }}>
              <SecurityIcon sx={{ color: colors.gold, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, fontFamily: "'Cinzel', serif" }}>
                Section 10: Business Sureties
              </Typography>
            </Box>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: colors.pearl, borderRadius: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: colors.navy, fontWeight: 700, fontFamily: "'Cinzel', serif" }}>First Surety</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField label="Full Name" fullWidth helperText="As shown on passport/ID" />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="ID / Passport Number" fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Contact Number" fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Physical Address" fullWidth multiline rows={2} />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: colors.pearl, borderRadius: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: colors.navy, fontWeight: 700, fontFamily: "'Cinzel', serif" }}>Second Surety</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField label="Full Name" fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="ID / Passport Number" fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Contact Number" fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Physical Address" fullWidth multiline rows={2} />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
              
              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: colors.pearl, borderRadius: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: colors.navy, fontWeight: 700, fontFamily: "'Cinzel', serif" }}>Third Surety</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField label="Full Name" fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="ID / Passport Number" fullWidth />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField label="Contact Number" fullWidth />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Physical Address" fullWidth multiline rows={2} />
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Section 11: Terms & Conditions */}
          <Box sx={{ mb: 5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 1, borderBottom: `2px solid ${colors.gold}` }}>
              <SecurityIcon sx={{ color: colors.gold, mr: 1 }} />
              <Typography variant="h5" sx={{ fontWeight: 700, color: colors.navy, fontFamily: "'Cinzel', serif" }}>
                Section 11: Terms & Conditions
              </Typography>
            </Box>
            
            <Alert 
              severity="info" 
              sx={{ mb: 3, borderRadius: 2, bgcolor: `${colors.premiumGold}1A`, color: colors.premiumGold, '& .MuiAlert-icon': { color: colors.premiumGold } }}
            >
              <strong>Secure Application Process</strong> — Your information is encrypted and protected.
            </Alert>
            
            <Paper sx={{ p: 3, mb: 3, maxHeight: 300, overflow: 'auto', bgcolor: colors.pearl, borderRadius: 3 }}>
              <Typography variant="h6" sx={{ color: colors.navy, fontWeight: 700, mb: 2, fontFamily: "'Cinzel', serif" }}>Terms & Conditions</Typography>
              <Typography variant="body2" paragraph>
                <strong>Warranty of authority to apply:</strong> I hereby warrant that I am duly authorised to sign this application form for and on behalf of the company.
              </Typography>
              <Typography variant="body2" paragraph>
                <strong>Consent:</strong> By checking this field I acknowledge and agree that all of the information I have provided is accurate, complete, and truthful.
              </Typography>
              <Typography variant="body2" paragraph>
                I give consent for Diamond Card to process my personal information, as per the Privacy Statement.
              </Typography>
              <Typography variant="body2" paragraph>
                I give consent, if required, for Diamond Card to check my credit record with any credit reference agency.
              </Typography>
              <Typography variant="body2" paragraph>
                I understand that due to FICA requirements, Diamond Card will require proof of your residential address.
              </Typography>
              <Typography variant="body2" paragraph>
                I understand that Diamond Card reserves the right to decline this application without providing a reason.
              </Typography>
            </Paper>

            <TextField
              label='Verification Code'
              fullWidth
              value={agree}
              onChange={(e) => setAgree(e.target.value)}
              sx={{ mb: 2 }}
              helperText='Type "I AGREE" to confirm your consent'
              placeholder='I AGREE'
            />
            
            <FormControlLabel
              control={<Checkbox checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)} sx={{ color: colors.gold }} />}
              label="I confirm that I have reviewed, understood and agree to the terms of this Application."
            />
          </Box>

          <Divider sx={{ my: 4 }} />

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              size="large"
              sx={{ 
                bgcolor: colors.gold,
                color: colors.navy,
                fontWeight: 800,
                borderRadius: 2,
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                fontFamily: "'Cinzel', serif",
                '&:hover': { bgcolor: colors.lightGold, transform: 'translateY(-2px)' },
                transition: 'all 0.3s ease',
              }}
              endIcon={<CheckCircleIcon />}
            >
              Submit Application
            </Button>
          </Box>
        </Paper>
      </Container>

      {/* Security Assurance Footer */}
      <Box sx={{ bgcolor: colors.pearl, py: 3, borderTop: `1px solid ${colors.gold}33` }}>
        <Container>
          <Grid container spacing={2} justifyContent="center">
            <Grid item>
              <Chip 
                icon={<SecurityIcon sx={{ color: colors.gold }} />} 
                label="256-bit SSL Encrypted" 
                sx={{ bgcolor: colors.white, fontWeight: 500, fontFamily: "'Cinzel', serif" }} 
              />
            </Grid>
            <Grid item>
              <Chip 
                icon={<SpeedIcon sx={{ color: colors.gold }} />} 
                label="Secure Application" 
                sx={{ bgcolor: colors.white, fontWeight: 500, fontFamily: "'Cinzel', serif" }} 
              />
            </Grid>
            <Grid item>
              <Chip 
                icon={<WorkspacePremiumIcon sx={{ color: colors.gold }} />} 
                label="Data Protected" 
                sx={{ bgcolor: colors.white, fontWeight: 500, fontFamily: "'Cinzel', serif" }} 
              />
            </Grid>
            <Grid item>
              <Chip 
                icon={<CheckCircleIcon sx={{ color: colors.gold }} />} 
                label="PCI Compliant" 
                sx={{ bgcolor: colors.white, fontWeight: 500, fontFamily: "'Cinzel', serif" }} 
              />
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Applications;